import { DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
// Frozen: the database CHECK constraint is generated from this tuple.
export const staffAvailabilityReasonCodes = Object.freeze([
    "ON_LEAVE",
    "OFF_SHIFT",
    "SICK",
    "TRAINING",
    "OTHER",
]);
/** The longest manual unavailability window: a longer absence is a coverage change, not a window. */
export const MAX_AVAILABILITY_WINDOW_MS = 90 * 24 * 3_600_000;
// A window may start slightly before `now`, to tolerate clock skew between callers (the same grace
// the TEMPORARY assignment window has).
const START_GRACE_MS = 60_000;
/** Whether the window makes its staff user unavailable at `now`: from `startsAt` (inclusive) to the earlier of `endsAt` and `endedAt` (exclusive). */
export function isUnavailableAt(window, now) {
    const end = window.endedAt === null
        ? window.endsAt.getTime()
        : Math.min(window.endsAt.getTime(), window.endedAt.getTime());
    return window.startsAt.getTime() <= now.getTime() && now.getTime() < end;
}
/** The staff users that have at least one window in force at `now`. */
export function unavailableStaffAt(windows, now) {
    return new Set(windows.filter((window) => isUnavailableAt(window, now)).map((window) => window.staffUserId));
}
/**
 * The rules of a new window: it ends after it starts, lasts at most 90 days, has not already
 * ended, and does not start before `now` (a minute of skew is tolerated). Violations of the shape
 * are `DomainValidationError`; none leaks a value.
 */
export function planAvailabilityWindow(input) {
    const { startsAt, endsAt, now } = input;
    if (Number.isNaN(startsAt.getTime()))
        throw new DomainValidationError("startsAt", "is invalid");
    if (Number.isNaN(endsAt.getTime()))
        throw new DomainValidationError("endsAt", "is invalid");
    if (endsAt.getTime() <= startsAt.getTime()) {
        throw new DomainValidationError("endsAt", "must be after startsAt");
    }
    if (endsAt.getTime() - startsAt.getTime() > MAX_AVAILABILITY_WINDOW_MS) {
        throw new DomainValidationError("endsAt", "must be within 90 days of startsAt");
    }
    if (endsAt.getTime() <= now.getTime())
        throw new DomainValidationError("endsAt", "is in the past");
    if (startsAt.getTime() < now.getTime() - START_GRACE_MS) {
        throw new DomainValidationError("startsAt", "is in the past");
    }
    return Object.freeze({ startsAt, endsAt });
}
/** A window can be ended once, and only while it has not already run out. */
export function assertWindowEndable(window, now) {
    if (window.endedAt !== null || window.endsAt.getTime() <= now.getTime()) {
        throw new DomainRuleViolationError("availability.window_not_open");
    }
}
